import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaSave, FaEdit, FaSpinner, FaPlus, FaTrash } from 'react-icons/fa';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import './ChartEditor.css';

const PRIMARY_COLOR = '#233e80';
const SECONDARY_PRIMARY_COLOR = '#1a6ed8';
const COLORS = [PRIMARY_COLOR, SECONDARY_PRIMARY_COLOR, '#3b9efc', '#1f5bd8', '#2563eb', '#0f2d73'];

export default function ChartEditor({ chartData, chartId, isOpen, onClose, onSave, isSaving = false }) {
  const [editedConfig, setEditedConfig] = useState({});
  const [editedData, setEditedData] = useState([]);
  const [activeTab, setActiveTab] = useState('data'); // 'data' or 'settings'

  useEffect(() => {
    if (chartData && isOpen) {
      const xKey = chartData.config?.xKey || 'name';
      const yKey = chartData.config?.yKey || 'value';

      setEditedConfig({
        title: chartData.config?.title || 'Chart',
        xAxisLabel: chartData.config?.xAxisLabel || xKey,
        yAxisLabel: chartData.config?.yAxisLabel || yKey,
        xKey,
        yKey,
        showDataPoints: chartData.config?.showDataPoints !== false,
        showGrid: chartData.config?.showGrid !== false,
        showLegend: chartData.config?.showLegend !== false,
        color: chartData.config?.color || PRIMARY_COLOR,
        legendLabel: chartData.config?.legendLabel || chartData.config?.yAxisLabel || yKey || 'Value'
      });

      const initialData = Array.isArray(chartData.data) && chartData.data.length > 0
        ? chartData.data.map(row => ({ ...row }))
        : [{ [xKey]: '', [yKey]: '' }];

      setEditedData(initialData);
      setActiveTab('data');
    }
  }, [chartData, isOpen]);

  if (!isOpen || !chartData) return null;

  const { type } = chartData;
  const config = editedConfig;
  const defaultRow = {
    [config.xKey || 'name']: '',
    [config.yKey || 'value']: ''
  };
  const rawData = editedData.length ? editedData : [defaultRow];
  const previewData = rawData.map(row => {
    const valueKey = config.yKey || 'value';
    const value = Number(row[valueKey]);
    return {
      ...row,
      [valueKey]: Number.isFinite(value) ? value : 0
    };
  });

  const handleSave = () => {
    const valueKey = config.yKey || 'value';
    const sanitizedData = rawData.map(row => {
      const newRow = { ...row };
      const rawValue = newRow[valueKey];
      const numericValue = rawValue === '' || rawValue === null || Number.isNaN(Number(rawValue))
        ? 0
        : Number(rawValue);
      newRow[valueKey] = numericValue;
      return newRow;
    });

    const updatedChartData = {
      ...chartData,
      data: sanitizedData,
      config: {
        ...chartData.config,
        ...editedConfig
      }
    };
    
    if (onSave) {
      onSave(updatedChartData, chartId);
    }
    onClose();
  };

  const handleDataEdit = (index, field, value) => {
    const xKey = config.xKey || 'name';
    const yKey = config.yKey || 'value';

    setEditedData(prev => {
      const templateRow = { [xKey]: '', [yKey]: '' };
      const rows = prev.length ? [...prev] : [templateRow];
      const currentRow = rows[index] ? { ...rows[index] } : { ...templateRow };
      currentRow[field] = value;
      rows[index] = currentRow;
      return rows;
    });
  };

  const handleAddRow = () => {
    const xKey = config.xKey || 'name';
    const yKey = config.yKey || 'value';
    setEditedData(prev => [...prev, { [xKey]: '', [yKey]: '' }]);
  };

  const handleRemoveRow = (index) => {
    setEditedData(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, rowIndex) => rowIndex !== index);
    });
  };

  const renderChartPreview = () => {
    const chartHeight = 400;
    const gradientId = `colorGradient-${chartId || Date.now()}`;
    const legendName = config.legendLabel || config.yAxisLabel || config.yKey || 'Value';

    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={previewData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              {config.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
              <XAxis 
                dataKey={config.xKey} 
                stroke="#64748b"
                label={config.xAxisLabel ? { value: config.xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
              />
              <YAxis 
                stroke="#64748b"
                label={config.yAxisLabel ? { value: config.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              {config.showLegend && <Legend />}
              <Bar dataKey={config.yKey} fill={config.color} radius={[8, 8, 0, 0]} name={legendName} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={previewData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              {config.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
              <XAxis 
                dataKey={config.xKey} 
                stroke="#64748b"
                label={config.xAxisLabel ? { value: config.xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
              />
              <YAxis 
                stroke="#64748b"
                label={config.yAxisLabel ? { value: config.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              {config.showLegend && <Legend />}
              <Line 
                type="monotone" 
                dataKey={config.yKey} 
                stroke={config.color} 
                strokeWidth={3}
                dot={config.showDataPoints ? { fill: config.color, r: 5 } : false}
                activeDot={{ r: 7 }}
                name={legendName}
              >
                {config.showDataPoints && (
                  <LabelList
                    dataKey={config.yKey}
                    position="top"
                    fill={config.color}
                    formatter={(value) => value}
                  />
                )}
              </Line>
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart>
              <Pie
                data={previewData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey={config.yKey}
              >
                {previewData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              {config.showLegend && <Legend />}
            </PieChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={previewData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              {config.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
              <XAxis 
                dataKey={config.xKey} 
                stroke="#64748b"
                label={config.xAxisLabel ? { value: config.xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
              />
              <YAxis 
                stroke="#64748b"
                label={config.yAxisLabel ? { value: config.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
              {config.showLegend && <Legend />}
              <Area 
                type="monotone" 
                dataKey={config.yKey} 
                stroke={config.color} 
                fill={`url(#${gradientId})`}
                strokeWidth={2}
                name={legendName}
              />
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={config.color} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={config.color} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return <div>Unsupported chart type</div>;
    }
  };

  return createPortal(
    <div className="chart-editor-overlay" onClick={onClose}>
      <div className="chart-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chart-editor-header">
          <h2 className="chart-editor-title">
            <FaEdit style={{ marginRight: '8px' }} />
            Edit Chart
          </h2>
          <button className="chart-editor-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="chart-editor-tabs">
          <button
            className={`chart-editor-tab ${activeTab === 'data' ? 'active' : ''}`}
            onClick={() => setActiveTab('data')}
          >
            Data
          </button>
          <button
            className={`chart-editor-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        <div className="chart-editor-content">
          {activeTab === 'data' && (
            <div className="chart-editor-data-section">
              <div className="chart-editor-preview">
                <h3>Preview</h3>
                <div className="chart-preview-container">
                  {renderChartPreview()}
                </div>
              </div>
              <div className="chart-editor-table">
                <h3>Chart Data</h3>
                <div className="chart-data-table-wrapper spreadsheet-wrapper">
                  <table className="chart-data-table spreadsheet-table">
                    <thead>
                      <tr>
                        <th className="spreadsheet-corner" />
                        <th className="spreadsheet-header">{config.xAxisLabel || config.xKey || 'X-Axis'}</th>
                        <th className="spreadsheet-header">{config.legendLabel || config.yAxisLabel || config.yKey || 'Y-Axis'}</th>
                        <th className="spreadsheet-header actions-column">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rawData.map((row, index) => (
                        <tr key={index}>
                          <td className="spreadsheet-row-header">{index + 1}</td>
                          <td>
                            <input
                              type="text"
                              value={editedData[index]?.[config.xKey] ?? ''}
                              onChange={(e) => handleDataEdit(index, config.xKey, e.target.value)}
                              className="chart-data-input spreadsheet-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editedData[index]?.[config.yKey] ?? ''}
                              onChange={(e) => handleDataEdit(index, config.yKey, e.target.value)}
                              className="chart-data-input spreadsheet-input"
                            />
                          </td>
                          <td className="spreadsheet-actions">
                            <button
                              type="button"
                              className="spreadsheet-remove-row"
                              onClick={() => handleRemoveRow(index)}
                              disabled={rawData.length <= 1}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="spreadsheet-add-row" onClick={handleAddRow}>
                  <FaPlus /> Add Row
                </button>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="chart-editor-settings-section">
              <div className="chart-editor-preview">
                <h3>Preview</h3>
                <div className="chart-preview-container">
                  {renderChartPreview()}
                </div>
              </div>
              <div className="chart-editor-form">
                <div className="chart-editor-form-group">
                  <label>Chart Title</label>
                  <input
                    type="text"
                    value={config.title || ''}
                    onChange={(e) => setEditedConfig({ ...editedConfig, title: e.target.value })}
                    className="chart-editor-input"
                  />
                </div>

                <div className="chart-editor-form-group">
                  <label>X-Axis Label</label>
                  <input
                    type="text"
                    value={config.xAxisLabel || ''}
                    onChange={(e) => setEditedConfig({ ...editedConfig, xAxisLabel: e.target.value })}
                    className="chart-editor-input"
                  />
                </div>

                <div className="chart-editor-form-group">
                  <label>Y-Axis Label</label>
                  <input
                    type="text"
                    value={config.yAxisLabel || ''}
                    onChange={(e) => setEditedConfig({ ...editedConfig, yAxisLabel: e.target.value })}
                    className="chart-editor-input"
                  />
                </div>

                <div className="chart-editor-form-group">
                  <label>Legend Label</label>
                  <input
                    type="text"
                    value={config.legendLabel || ''}
                    onChange={(e) => setEditedConfig({ ...editedConfig, legendLabel: e.target.value })}
                    className="chart-editor-input"
                  />
                </div>

                {type === 'line' && (
                  <div className="chart-editor-form-group">
                    <label className="chart-editor-checkbox-label">
                      <input
                        type="checkbox"
                        checked={config.showDataPoints !== false}
                        onChange={(e) => setEditedConfig({ ...editedConfig, showDataPoints: e.target.checked })}
                        className="chart-editor-checkbox"
                      />
                      Show Data Points
                    </label>
                  </div>
                )}

                <div className="chart-editor-form-group">
                  <label className="chart-editor-checkbox-label">
                    <input
                      type="checkbox"
                      checked={config.showGrid !== false}
                      onChange={(e) => setEditedConfig({ ...editedConfig, showGrid: e.target.checked })}
                      className="chart-editor-checkbox"
                    />
                    Show Grid
                  </label>
                </div>

                <div className="chart-editor-form-group">
                  <label className="chart-editor-checkbox-label">
                    <input
                      type="checkbox"
                      checked={config.showLegend !== false}
                      onChange={(e) => setEditedConfig({ ...editedConfig, showLegend: e.target.checked })}
                      className="chart-editor-checkbox"
                    />
                    Show Legend
                  </label>
                </div>

                <div className="chart-editor-form-group">
                  <label>Chart Color</label>
                  <div className="chart-editor-color-input">
                    <input
                      type="color"
                      value={config.color || PRIMARY_COLOR}
                      onChange={(e) => setEditedConfig({ ...editedConfig, color: e.target.value })}
                      className="chart-editor-color-picker"
                    />
                    <input
                      type="text"
                      value={config.color || PRIMARY_COLOR}
                      onChange={(e) => setEditedConfig({ ...editedConfig, color: e.target.value })}
                      className="chart-editor-color-text"
                      placeholder={PRIMARY_COLOR}
                    />
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>

        <div className="chart-editor-footer">
          <button className="chart-editor-cancel" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="chart-editor-save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <FaSpinner className="chart-editor-spinner" style={{ marginRight: '8px' }} />
                Updating Chart...
              </>
            ) : (
              <>
                <FaSave style={{ marginRight: '8px' }} />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Loading Overlay */}
        {isSaving && (
          <div className="chart-editor-saving-overlay">
            <div className="chart-editor-saving-content">
              <FaSpinner className="chart-editor-saving-spinner" />
              <p>Updating Chart...</p>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

